import { QuestionTypeChip } from '@exam-management/components';
import {
  QBankStructureLevelCategory,
  QuestionGroupType,
  TagCategory,
  useGetQuestionToChangeByIdQuery,
  useGetTopicAndSubTopicByChapterIdQuery,
} from '@exam-management/graphql';
import {
  type OptionSelectionProps,
  Chip,
  Divider,
  Flex,
  Loading,
  SearchSelection,
  Text,
} from '@shared/components';
import { useMemo, useState } from 'react';

import { useQuestionChangeInformation } from '../../providers';

interface QuestionChangeInformationSectionProps {
  questionId: string;
}

export default function QuestionChangeInformationSection(
  props: Readonly<QuestionChangeInformationSectionProps>,
) {
  const { questionId } = props;

  const [topic, setTopic] = useState<OptionSelectionProps | null>(null);
  const [subTopic, setSubTopic] = useState<OptionSelectionProps | null>(null);
  const [hasTopic, setHasTopic] = useState<boolean>(true);

  const {
    changeTopicId,
    changeSubTopicId,
    handleSetQBankStructureId,
    handleInformationLoaded,
    questionGroupSize,
  } = useQuestionChangeInformation();

  const { data: currentQuestionResult, loading: isCurrentQuestionLoading } =
    useGetQuestionToChangeByIdQuery({
      onCompleted: (data) => {
        const courseId = data.qctGetQuestionById?.refQBankStructureId ?? '';
        handleSetQBankStructureId(courseId);
      },
      variables: {
        qctGetQuestionById: questionId,
      },
      fetchPolicy: 'network-only',
    });

  const question = currentQuestionResult?.qctGetQuestionById;
  const questionStructures = question?.questionStructures ?? [];

  const chapterLevelItem = questionStructures.find(
    (structure) =>
      structure.levelItem?.qBankStructureLevel?.category === QBankStructureLevelCategory.Chapter,
  )?.levelItem;
  const chapterId = chapterLevelItem?.id ?? '';

  const { data: chapterLevelItemResult, loading: isTopicAndSubTopicLoading } =
    useGetTopicAndSubTopicByChapterIdQuery({
      variables: {
        qbsGetQBankStructureLevelItemChildrenById: chapterId,
      },
      onCompleted: () => {
        const newTopic = question?.questionStructures?.find(
          (structure) =>
            structure.levelItem?.qBankStructureLevel?.category ===
            QBankStructureLevelCategory.Topic,
        )?.levelItem;

        if (newTopic) {
          setHasTopic(true);
          setTopic({ label: newTopic?.tag?.name ?? 'ไม่ระบุ', value: newTopic?.id });
          changeTopicId(newTopic?.id);
        } else {
          setTopic(null);
          setHasTopic(false);
        }

        const newSubTopic = question?.questionStructures?.find(
          (structure) =>
            structure.levelItem?.qBankStructureLevel?.category ===
            QBankStructureLevelCategory.SubTopic,
        )?.levelItem;

        if (newSubTopic) {
          setSubTopic({ label: newSubTopic?.tag?.name ?? '', value: newSubTopic?.id });
          changeSubTopicId(newSubTopic?.id);
        }

        handleInformationLoaded();
      },
      fetchPolicy: 'network-only',
      skip: !question,
    });

  const chapterLevelItems = chapterLevelItemResult?.qbsGetQBankStructureLevelItemChildrenById ?? [];

  const topicOptions = useMemo(() => {
    const topicLevelItem = chapterLevelItems.filter(
      (levelItem) => levelItem.qBankStructureLevel?.category === QBankStructureLevelCategory.Topic,
    );
    return topicLevelItem.map((levelItem) => ({
      label: levelItem.tag?.name ?? 'ไม่ระบุ',
      value: levelItem.id,
    }));
  }, [chapterLevelItems]);

  const subTopicOptions = useMemo(() => {
    let filteredSubTopics = [];

    if (topic) {
      filteredSubTopics = chapterLevelItems.filter(
        (levelItem) =>
          levelItem.qBankStructureLevel?.category === QBankStructureLevelCategory.SubTopic &&
          levelItem.parentLevelItemId === topic.value,
      );
    } else {
      filteredSubTopics = chapterLevelItems.filter(
        (levelItem) =>
          levelItem.qBankStructureLevel?.category === QBankStructureLevelCategory.SubTopic,
      );
    }

    return filteredSubTopics.map((levelItem) => ({
      label: levelItem.tag?.name ?? '',
      value: levelItem.id,
    }));
  }, [topicOptions, topic]);

  if (isCurrentQuestionLoading || isTopicAndSubTopicLoading) {
    return <Loading />;
  }

  const questionTags = question?.tags ?? [];
  const refCode = question?.refCode;

  const calculation =
    questionTags.find((tag) => tag.category === TagCategory.Calculation)?.name ?? '';
  const difficulty =
    questionTags.find((tag) => tag.category === TagCategory.Difficulty)?.name ?? '';

  const questionType =
    question?.questionGroup?.type === QuestionGroupType.ItemSet ? 'item-set' : 'single';

  const chapterName = chapterLevelItem?.tag?.name ?? 'ไม่ระบุ';

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        gap: theme.spacing(4),
        p: theme.spacing(4),
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.shape.rounded(2),
        backgroundColor: theme.palette.grey[50],
      })}
    >
      <Flex sx={{ justifyContent: 'space-between' }}>
        <Text variant='body1Semibold'>{chapterName}</Text>
        <Chip
          label={refCode}
          size='small'
          sx={(theme) => ({
            border: `1px solid ${theme.palette.grey[300]}`,
            backgroundColor: theme.palette.grey[200],
          })}
        />
      </Flex>
      <Divider />

      <Flex sx={(theme) => ({ gap: theme.spacing(4), alignItems: 'center' })}>
        <Flex sx={(theme) => ({ gap: theme.spacing(2) })}>
          <Text variant='body2'>QType:</Text>
          <QuestionTypeChip type={questionType} questionSize={questionGroupSize} />
        </Flex>
        <Divider orientation='vertical' flexItem />
        <Flex sx={(theme) => ({ gap: theme.spacing(2) })}>
          <Text variant='body2'>ระดับความยาก:</Text>
          <Text variant='body2Semibold'>
            {difficulty}, {calculation}
          </Text>
        </Flex>
      </Flex>
      <Divider />

      <Flex sx={(theme) => ({ width: '100%', gap: theme.spacing(4) })}>
        <SearchSelection
          label='Topic'
          value={topic}
          options={topicOptions}
          sx={{ flex: 1 }}
          onChange={(_, item) => {
            changeTopicId(item?.value?.toString());
            setTopic(item);
            setSubTopic(null);
          }}
          placeholder={hasTopic ? 'Select Topic' : 'ไม่เปิดใช้งาน'}
          disabled={!hasTopic}
        />
        <SearchSelection
          label='Sub-Topic'
          value={subTopic}
          options={subTopicOptions}
          sx={{ flex: 1 }}
          onChange={(_, item) => {
            changeSubTopicId(item?.value?.toString());
            setSubTopic(item);
          }}
          placeholder='Select Sub-Topic'
          disabled={!topic && hasTopic}
        />
      </Flex>
    </Flex>
  );
}
